import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
  }),
  center: {
    x: 0,
  },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
  }),
};

export default function CertLightbox({ cert, onClose }) {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);

  const images = cert?.images || [];
  const hasImages = images.length > 0;
  const hasMultipleImages = images.length > 1;

  useEffect(() => {
    setCurrentIndex(0);
    setSlideDirection(1);
  }, [cert]);

  useEffect(() => {
    if (!hasImages) return;

    images.forEach((imageSrc) => {
      const img = new Image();
      img.src = imageSrc;
    });
  }, [hasImages, images]);

  if (!cert) return null;

  const nextImage = () => {
    setSlideDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevImage = () => {
    setSlideDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const handleDotClick = (index) => {
    if (index === currentIndex) return;
    setSlideDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative glass rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Image Viewer */}
        <div className="relative w-full h-[72vh] overflow-hidden bg-black">
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full glass flex items-center justify-center text-heading hover:bg-accent-500/20 transition-colors"
            aria-label={t.certifications.closePreview}
          >
            <X size={20} />
          </button>

          <AnimatePresence initial={false} custom={slideDirection} mode="sync">
            {hasImages ? (
              <motion.img
                key={currentIndex}
                src={images[currentIndex]}
                alt={`${cert.title} - slide ${currentIndex + 1}`}
                custom={slideDirection}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'tween', duration: 0.28, ease: 'easeInOut' }}
                className="absolute inset-0 block w-full h-full object-contain select-none"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-linear-to-br from-accent-500/20 to-cyan-500/20">
                <Award size={64} className="text-accent-400/40" />
              </div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          {hasMultipleImages && (
            <>
              <button
                onClick={prevImage}
                className="absolute top-1/2 left-4 -translate-y-1/2 z-10 w-10 h-10 rounded-full glass flex items-center justify-center text-heading hover:bg-accent-500/20 transition-colors"
                aria-label={t.certifications.previousImage}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextImage}
                className="absolute top-1/2 right-4 -translate-y-1/2 z-10 w-10 h-10 rounded-full glass flex items-center justify-center text-heading hover:bg-accent-500/20 transition-colors"
                aria-label={t.certifications.nextImage}
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
          
          {/* Dots */}
          {hasMultipleImages && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotClick(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    currentIndex === index ? 'bg-accent-500' : 'bg-white/40 hover:bg-white/60'
                  }`}
                  aria-label={`${t.certifications.goToImage} ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-6 pb-7 shrink-0">
          <h3 className="text-heading text-xl font-bold mb-1">{cert.title}</h3>
          <p className="text-muted text-sm mb-4">{cert.issuer}</p>

          {cert.verifyUrl ? (
            <a
              href={cert.verifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-linear-to-r from-accent-500 to-cyan-500 text-white font-medium text-sm hover:-translate-y-0.5 transition-transform"
            >
              <ExternalLink size={16} />
              {t.certifications.verify}
            </a>
          ) : (
            <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-surface text-faint text-sm cursor-not-allowed">
              <ExternalLink size={16} />
              {t.certifications.verifyNA}
            </span>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
